import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Loader2, Plus, SlidersHorizontal } from 'lucide-react';
import type { Driver06Data, ICFBranchRow, ICFSection, BranchSourceRow } from '../../../api/apiTypesConductRisk';
import { EditActionBar } from '../shared/EditActionBar';
import { ExportXlsxButton } from '../shared/ExportXlsxButton';
import { useConductRiskDriver } from '../../../api/hooks/useConductRiskDriver';
import { fetchExportConductRiskDriverXlsx } from '../../../api/functionApi';
import { formatHighlights } from '../../../utils/Format';

/* ─── Soglie ICF di default (banda rischio medio, come da specifica Compliance).
 * Le altre suddivisioni interne della legenda (10, 55, 70) restano fisse. */
const ICF_DEFAULT_LOWER = 25;
const ICF_DEFAULT_UPPER = 40;

/* ─── Factory righe vuote per "Aggiungi nuovo" (tutti i campi del tipo) ── */
const emptyICFBranchRow = (): ICFBranchRow => ({
  regional_division:        '',
  branches:                 0,
  awarded_branches:         0,
  icf_medium_risk:          0,
  usury_high:               0,
  aml_high:                 0,
  investment_services_high: 0,
  transparency_high:        0,
  ivass_high:               0,
  icf_medium_risk_pct:      0,
});

const emptyBranchSourceRow = (): BranchSourceRow => ({
  regional_division: '',
  branch_code:       '',
  awarded:           false,
  icf:               0,
  usura:             0,
  aml:               0,
  investimenti:      0,
  trasparenza:       0,
  ivass:             0,
});

const TIPOLOGIA_LABEL: Record<string, string> = {
  RETAIL:          'Retail',
  EXCLUSIVE:       'Exclusive',
  IMPRESE:         'Imprese',
  DIGITALE_REMOTA: 'Digitale Remota',
  TERZO_SETTORE:   'Terzo Settore',
  AGRIBUSINESS:    'Agribusiness',
  IMPACT:          'Impact',
};

// Etichette sempre in MAIUSCOLO: sia quelle mappate sia le chiavi non mappate
// che arrivano grezze dal BE, così la resa è uniforme in tutta la vista.
const tipoLabel = (k: string) => (TIPOLOGIA_LABEL[k] ?? k).toUpperCase();

const fmtN    = (n: number) => n.toLocaleString('it-IT');
const fmtPct2 = (n: number) => `${n.toFixed(2)}%`;
// Null-safe per la vista "Dettaglio filiali": i valori grezzi dei source_rows
// possono essere null (indicatore non disponibile per la filiale).
const fmtNn   = (n: number | null) => (n == null ? '—' : fmtN(n));

const numCls    = 'w-16 text-right bg-white text-[#1B365D] border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-[#1B365D]/50 tabular-nums';
const numClsSm  = 'w-12 text-right bg-white text-[#1B365D] border border-[#1B365D]/20 rounded px-1 py-0.5 text-xs focus:outline-none focus:border-[#1B365D]/50 tabular-nums';
const txtCls    = 'w-full min-w-[6rem] bg-white border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-xs text-[#1B365D] focus:outline-none focus:border-[#1B365D]/50';

/* ─── Helpers ────────────────────────────────────────────── */
const RiskCell = ({ n, isEditing, onChange }: { n: number; isEditing: boolean; onChange?: (v: string) => void }) => {
  if (isEditing) {
    return (
      <input
        type="number"
        className={numClsSm}
        value={n}
        onChange={e => onChange?.(e.target.value)}
      />
    );
  }
  return n === 0
    ? <span className="text-gray-300">—</span>
    : <span className="inline-flex px-1.5 py-0.5 rounded bg-amber-50 border border-amber-100 text-amber-700 font-bold text-[10px]">{n}</span>;
};

const FooterCell = ({ n }: { n: number }) =>
  n === 0 ? <span className="text-white/40">—</span> : <span>{n}</span>;

/* ─── Main ───────────────────────────────────────────────── */
type SegmentKey = string;          // chiave del segmento in by_segment (RETAIL, EXCLUSIVE, ...)
type ActiveTab  = SegmentKey | '__GLOBAL__';

const CorrelazioneICFContainer = ({ reportId }: { reportId: string }) => {
  const { data: remoteData, loading, error, save, isReadOnly } = useConductRiskDriver<Driver06Data>(reportId, 6);

  const [data,      setData]      = useState<Driver06Data | null>(null);
  const [savedData, setSavedData] = useState<Driver06Data | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTabIdx, setActiveTabIdx] = useState(0);
  // Vista tabella: aggregato per Direzione (default) o dettaglio per-filiale
  // (source_rows, la popolazione completa editabile).
  const [viewMode, setViewMode] = useState<'agg' | 'detail'>('agg');
  const [icfLower,     setIcfLower]     = useState<number>(ICF_DEFAULT_LOWER);
  const [icfUpper,     setIcfUpper]     = useState<number>(ICF_DEFAULT_UPPER);
  const [autoSaving,   setAutoSaving]   = useState(false);
  const debounceRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Le soglie utente sopravvivono ai refetch: solo il primo mount le
  // pesca dal BE (come per la soglia in Driver 4).
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!remoteData) return;
    setData(remoteData);
    setSavedData(remoteData);
    if (!initializedRef.current) {
      setIcfLower(remoteData.thresholds?.icf_band_low  ?? ICF_DEFAULT_LOWER);
      setIcfUpper(remoteData.thresholds?.icf_band_high ?? ICF_DEFAULT_UPPER);
      initializedRef.current = true;
    }
  }, [remoteData]);

  // Autosave debounced delle soglie della banda ICF (~600ms). Attivo solo fuori
  // editing: in editing le soglie vengono salvate col bottone Salva insieme alle
  // altre modifiche, per evitare race con l'edit locale in corso.
  useEffect(() => {
    if (!data || !savedData || isEditing) return;
    const savedLower = savedData.thresholds?.icf_band_low  ?? ICF_DEFAULT_LOWER;
    const savedUpper = savedData.thresholds?.icf_band_high ?? ICF_DEFAULT_UPPER;
    if (icfLower === savedLower && icfUpper === savedUpper) return;
    // Vincolo BE: icf_band_low < icf_band_high (altrimenti 422): non autosalvo
    // finche' i valori non sono coerenti.
    if (icfLower >= icfUpper) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const payload: Driver06Data = {
        ...data,
        thresholds: { ...data.thresholds, icf_band_low: icfLower, icf_band_high: icfUpper },
      };
      setAutoSaving(true);
      try {
        await save(payload);
        setSavedData(payload);
      } catch (err) {
        console.error('Autosave soglie ICF fallito:', err);
        setIcfLower(savedLower);
        setIcfUpper(savedUpper);
      } finally {
        setAutoSaving(false);
      }
    }, 600);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [icfLower, icfUpper, savedData, isEditing, data, save]);

  const handleAnnulla = () => { if (savedData) setData(savedData); setIsEditing(false); };
  const handleSalva   = async () => {
    if (!data) return;
    // Se la narrativa e' stata riscritta, aggancio highlights_thresholds alle
    // soglie correnti cosi' il BE puo' azzerare highlights_stale.
    const highlightsChanged = !!savedData && data.highlights !== savedData.highlights;
    const payload: Driver06Data = highlightsChanged
      ? { ...data, highlights_thresholds: data.thresholds }
      : data;
    await save(payload);
    setSavedData(payload);
    setData(payload);
    setIsEditing(false);
  };

  const updateRow = (i: number, field: keyof ICFBranchRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => {
      if (!prev) return prev;
      const segmentKeysLocal = Object.keys(prev.by_segment);
      const tabsLocal: { label: string; key: ActiveTab }[] = [
        ...segmentKeysLocal.map(k => ({ label: tipoLabel(k), key: k as ActiveTab })),
        { label: 'ICF Globale', key: '__GLOBAL__' as ActiveTab },
      ];
      const tab = tabsLocal[activeTabIdx] ?? tabsLocal[0];
      const global = tab.key === '__GLOBAL__';
      if (global) {
        return {
          ...prev,
          global_aggregate: {
            ...prev.global_aggregate,
            rows: prev.global_aggregate.rows.map((r, ri) => ri === i ? { ...r, [field]: val } : r),
          },
        };
      }
      return {
        ...prev,
        by_segment: {
          ...prev.by_segment,
          [tab.key]: {
            ...prev.by_segment[tab.key],
            rows: prev.by_segment[tab.key].rows.map((r, ri) => ri === i ? { ...r, [field]: val } : r),
          },
        },
      };
    });
  };

  /** Edit del nome (Direzione) di una riga aggregata. Free-form, nessun ricalcolo. */
  const updateRowName = (i: number, value: string) => {
    setData(prev => {
      if (!prev) return prev;
      const segmentKeysLocal = Object.keys(prev.by_segment);
      const tabsLocal: { label: string; key: ActiveTab }[] = [
        ...segmentKeysLocal.map(k => ({ label: tipoLabel(k), key: k as ActiveTab })),
        { label: 'ICF Globale', key: '__GLOBAL__' as ActiveTab },
      ];
      const tab = tabsLocal[activeTabIdx] ?? tabsLocal[0];
      if (tab.key === '__GLOBAL__') {
        return {
          ...prev,
          global_aggregate: {
            ...prev.global_aggregate,
            rows: prev.global_aggregate.rows.map((r, ri) => ri === i ? { ...r, regional_division: value } : r),
          },
        };
      }
      return {
        ...prev,
        by_segment: {
          ...prev.by_segment,
          [tab.key]: {
            ...prev.by_segment[tab.key],
            rows: prev.by_segment[tab.key].rows.map((r, ri) => ri === i ? { ...r, regional_division: value } : r),
          },
        },
      };
    });
  };

  /** Aggiunge una riga vuota alla tabella aggregata del tab corrente
   *  (global_aggregate o by_segment[tab], stessa logica di updateRow). */
  const addRow = () => {
    setData(prev => {
      if (!prev) return prev;
      const segmentKeysLocal = Object.keys(prev.by_segment);
      const tabsLocal: { label: string; key: ActiveTab }[] = [
        ...segmentKeysLocal.map(k => ({ label: tipoLabel(k), key: k as ActiveTab })),
        { label: 'ICF Globale', key: '__GLOBAL__' as ActiveTab },
      ];
      const tab = tabsLocal[activeTabIdx] ?? tabsLocal[0];
      if (tab.key === '__GLOBAL__') {
        return {
          ...prev,
          global_aggregate: {
            ...prev.global_aggregate,
            rows: [...prev.global_aggregate.rows, emptyICFBranchRow()],
          },
        };
      }
      return {
        ...prev,
        by_segment: {
          ...prev.by_segment,
          [tab.key]: {
            ...prev.by_segment[tab.key],
            rows: [...prev.by_segment[tab.key].rows, emptyICFBranchRow()],
          },
        },
      };
    });
  };

  /** Aggiunge una riga vuota alla popolazione sorgente del segmento indicato. */
  const addSourceRow = (segmentKey: string) => {
    setData(prev => {
      if (!prev) return prev;
      const seg = prev.source_rows[segmentKey] ?? [];
      return {
        ...prev,
        source_rows: { ...prev.source_rows, [segmentKey]: [...seg, emptyBranchSourceRow()] },
      };
    });
  };

  /** Edit numerico dei totali (tfoot) della tabella aggregata. */
  const updateTotals = (field: keyof ICFBranchRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => {
      if (!prev) return prev;
      const segmentKeysLocal = Object.keys(prev.by_segment);
      const tabsLocal: { label: string; key: ActiveTab }[] = [
        ...segmentKeysLocal.map(k => ({ label: tipoLabel(k), key: k as ActiveTab })),
        { label: 'ICF Globale', key: '__GLOBAL__' as ActiveTab },
      ];
      const tab = tabsLocal[activeTabIdx] ?? tabsLocal[0];
      if (tab.key === '__GLOBAL__') {
        return {
          ...prev,
          global_aggregate: { ...prev.global_aggregate, totals: { ...prev.global_aggregate.totals, [field]: val } },
        };
      }
      return {
        ...prev,
        by_segment: {
          ...prev.by_segment,
          [tab.key]: { ...prev.by_segment[tab.key], totals: { ...prev.by_segment[tab.key].totals, [field]: val } },
        },
      };
    });
  };

  /** Edit del nome (Direzione) dei totali (tfoot). */
  const updateTotalsName = (value: string) => {
    setData(prev => {
      if (!prev) return prev;
      const segmentKeysLocal = Object.keys(prev.by_segment);
      const tabsLocal: { label: string; key: ActiveTab }[] = [
        ...segmentKeysLocal.map(k => ({ label: tipoLabel(k), key: k as ActiveTab })),
        { label: 'ICF Globale', key: '__GLOBAL__' as ActiveTab },
      ];
      const tab = tabsLocal[activeTabIdx] ?? tabsLocal[0];
      if (tab.key === '__GLOBAL__') {
        return {
          ...prev,
          global_aggregate: { ...prev.global_aggregate, totals: { ...prev.global_aggregate.totals, regional_division: value } },
        };
      }
      return {
        ...prev,
        by_segment: {
          ...prev.by_segment,
          [tab.key]: { ...prev.by_segment[tab.key], totals: { ...prev.by_segment[tab.key].totals, regional_division: value } },
        },
      };
    });
  };

  const updateHighlights = (value: string) => {
    setData(prev => (prev ? { ...prev, highlights: value } : prev));
  };

  /** Edit della popolazione sorgente per-filiale (vista "Dettaglio filiali").
   *  A differenza di updateRow (che tocca le tabelle aggregate), qui si edita
   *  direttamente `source_rows`, da cui il BE ricalcola by_segment/global_aggregate. */
  const updateSourceRow = (segmentKey: string, index: number, field: keyof BranchSourceRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => {
      if (!prev) return prev;
      const seg = prev.source_rows[segmentKey];
      if (!seg) return prev;
      return {
        ...prev,
        source_rows: { ...prev.source_rows, [segmentKey]: seg.map((r, i) => i === index ? { ...r, [field]: val } : r) },
      };
    });
  };

  /** Edit dei campi testuali (Filiale, Direzione) della popolazione sorgente. */
  const updateSourceRowText = (segmentKey: string, index: number, field: keyof BranchSourceRow, value: string) => {
    setData(prev => {
      if (!prev) return prev;
      const seg = prev.source_rows[segmentKey];
      if (!seg) return prev;
      return {
        ...prev,
        source_rows: { ...prev.source_rows, [segmentKey]: seg.map((r, i) => i === index ? { ...r, [field]: value } : r) },
      };
    });
  };

  const toggleAwarded = (segmentKey: string, index: number) => {
    setData(prev => {
      if (!prev) return prev;
      const seg = prev.source_rows[segmentKey];
      if (!seg) return prev;
      return {
        ...prev,
        source_rows: { ...prev.source_rows, [segmentKey]: seg.map((r, i) => i === index ? { ...r, awarded: !r.awarded } : r) },
      };
    });
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-12 h-12 border-[5px] border-gray-200 border-t-[#1B365D] rounded-full animate-spin" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-gray-400">Nessun dato disponibile per questo driver.</div>;
  }

  const segmentKeys = Object.keys(data.by_segment);
  const TABS: { label: string; key: ActiveTab }[] = [
    ...segmentKeys.map(k => ({ label: tipoLabel(k), key: k as ActiveTab })),
    { label: 'ICF Globale', key: '__GLOBAL__' as ActiveTab },
  ];

  const activeTab = TABS[activeTabIdx] ?? TABS[0];
  const isGlobal  = activeTab.key === '__GLOBAL__';

  const section: ICFSection = isGlobal
    ? data.global_aggregate
    : data.by_segment[activeTab.key];

  const totals = section.totals;

  // Righe della vista "Dettaglio filiali": source_rows del segmento attivo,
  // oppure tutti i segmenti concatenati nel tab ICF Globale. Traccio
  // (segmentKey, index) per poter editare la riga corretta in source_rows.
  const detailRows: { segmentKey: string; index: number; row: BranchSourceRow }[] =
    viewMode !== 'detail'
      ? []
      : isGlobal
        ? Object.keys(data.source_rows).flatMap(k =>
            (data.source_rows[k] ?? []).map((row, index) => ({ segmentKey: k, index, row })))
        : (data.source_rows[activeTab.key] ?? []).map((row, index) => ({ segmentKey: activeTab.key, index, row }));

  const thresholdInputCls = 'w-14 text-right text-sm font-bold text-[#1B365D] border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-[#1B365D]/40 disabled:opacity-50 disabled:cursor-not-allowed tabular-nums';

  return (
    <div className="p-6 space-y-5">

      {/* Soglie fasce di rischio ICF (parametrizzabili, autosave) */}
      <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100">
        <div className="w-8 h-8 rounded-lg bg-[#1B365D]/5 flex items-center justify-center shrink-0">
          <SlidersHorizontal className="w-4 h-4 text-[#1B365D]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-[#1B365D]">Soglie fasce di rischio ICF</p>
          <p className="text-[10px] text-gray-400 mt-0.5">
            Definiscono la <span className="font-semibold">Fascia intermedia</span> (rischio medio).
            Sotto il limite inferiore = rischio basso, sopra il limite superiore = rischio alto.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {autoSaving && (
            <Loader2 className="w-3.5 h-3.5 text-[#1B365D]/60 animate-spin" />
          )}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">Da</span>
            <input
              type="number"
              min={0}
              max={100}
              value={icfLower}
              disabled={isEditing || isReadOnly}
              onChange={e => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) setIcfLower(Math.max(0, Math.min(100, v)));
              }}
              className={thresholdInputCls}
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">A</span>
            <input
              type="number"
              min={0}
              max={100}
              value={icfUpper}
              disabled={isEditing || isReadOnly}
              onChange={e => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v)) setIcfUpper(Math.max(0, Math.min(100, v)));
              }}
              className={thresholdInputCls}
            />
          </div>
        </div>
      </div>

      {icfLower >= icfUpper && (
        <p className="text-[11px] text-red-600 -mt-3">Il limite inferiore della banda ICF deve essere minore del limite superiore.</p>
      )}

      {/* Fasce di rischio ICF (legenda) — 6 fasce come da specifica Compliance.
       * Solo i confini della fascia intermedia sono parametrizzabili;
       * le altre suddivisioni interne (10, 55, 70) restano fisse. */}
      <div>
        <div className="flex items-center gap-1 text-[9px] text-gray-400 mb-1">
          <span className="flex-[2] text-center">Sotto soglia di attenzione</span>
          <span className="flex-1 text-center">Fascia intermedia</span>
          <span className="flex-[3] text-center">Sopra soglia di attenzione</span>
        </div>
        <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide">
          <span className="flex-1 text-center px-2 py-1 rounded-l-lg bg-emerald-50 text-emerald-700 border border-emerald-100">
            0 – 10 · Irrilevante
          </span>
          <span className="flex-1 text-center px-2 py-1 bg-lime-50 text-lime-700 border border-lime-100">
            10 – {icfLower} · Basso
          </span>
          <span className="flex-1 text-center px-2 py-1 bg-amber-50 text-amber-700 border border-amber-100">
            {icfLower} – {icfUpper} · Medio
          </span>
          <span className="flex-1 text-center px-2 py-1 bg-orange-50 text-orange-700 border border-orange-100">
            {icfUpper} – 55 · Alto
          </span>
          <span className="flex-1 text-center px-2 py-1 bg-red-50 text-red-700 border border-red-100">
            55 – 70 · Molto alto
          </span>
          <span className="flex-1 text-center px-2 py-1 rounded-r-lg bg-red-100 text-red-800 border border-red-200">
            ≥ 70 · Estremo
          </span>
        </div>
      </div>

      {/* Tabs + edit bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1 p-1 bg-gray-100 rounded-xl overflow-x-auto flex-1 min-w-0">
          {TABS.map((tab, i) => (
            <button
              key={tab.label}
              onClick={() => setActiveTabIdx(i)}
              className={`flex-shrink-0 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${
                activeTabIdx === i ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <ExportXlsxButton
          variant="navy"
          fetchBlob={() => fetchExportConductRiskDriverXlsx(reportId, 'Driver06')}
          fileName={`Correlazione_ICF_${data.period}`}
        />
        <EditActionBar isEditing={isEditing} onProcedi={() => setIsEditing(true)} onAnnulla={handleAnnulla} onSalva={handleSalva} disabled={isReadOnly} />
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <div className="text-2xl font-bold text-[#1B365D]">{fmtN(totals.branches)}</div>
          <div className="text-[10px] text-gray-400 mt-1">Filiali totali</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <div className="text-2xl font-bold text-[#1B365D]">{fmtN(totals.awarded_branches)}</div>
          <div className="text-[10px] text-gray-400 mt-1">Filiali a premio</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <div className="text-2xl font-bold text-amber-600">{fmtN(totals.icf_medium_risk)}</div>
          <div className="text-[10px] text-gray-400 mt-1">Con ICF rischio medio ({fmtPct2(totals.icf_medium_risk_pct)})</div>
        </div>
      </div>

      {/* Avviso narrativa non allineata alle soglie correnti */}
      {data.highlights_stale && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">
            Gli <span className="font-semibold">Highlights</span> sono stati generati con soglie diverse da quelle attualmente applicate: le tabelle sono ricalcolate ma la narrativa potrebbe citare numeri non aggiornati. Rigenera/aggiorna gli Highlights per allinearli.
          </p>
        </div>
      )}

      {/* Highlights */}
      <div className="px-4 py-3 rounded-xl bg-[#1B365D]/5 border border-[#1B365D]/10 text-xs text-[#1B365D]/80 leading-relaxed">
        <span className="font-semibold">Highlights: </span>
        {isEditing
          ? <textarea
              className="w-full mt-1 p-2 text-xs text-[#1B365D]/80 leading-relaxed border border-[#1B365D]/20 rounded-lg resize-y focus:outline-none focus:border-[#1B365D]/40 bg-white/70 min-h-[120px]"
              value={data.highlights}
              onChange={e => updateHighlights(e.target.value)}
            />
          : <span className="whitespace-pre-wrap">{formatHighlights(data.highlights)}</span>}
      </div>

      {/* Table */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${activeTab.key}-${viewMode}`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.18 }}
        >
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
              <p className="text-xs font-semibold text-[#1B365D]">
                Correlazione ICF — {activeTab.label} — {data.period}
                {viewMode === 'detail' && (
                  <span className="ml-2 text-[10px] font-normal text-gray-400">
                    · {detailRows.length} filiali
                  </span>
                )}
              </p>
              {/* Toggle vista: aggregato per Direzione vs dettaglio per-filiale */}
              <div className="flex gap-1 p-0.5 bg-gray-100 rounded-lg shrink-0">
                <button
                  onClick={() => setViewMode('agg')}
                  className={`py-1 px-2.5 rounded-md text-[11px] font-semibold transition-all ${
                    viewMode === 'agg' ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  Aggregato per Direzione
                </button>
                <button
                  onClick={() => setViewMode('detail')}
                  className={`py-1 px-2.5 rounded-md text-[11px] font-semibold transition-all ${
                    viewMode === 'detail' ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  Dettaglio filiali
                </button>
              </div>
            </div>
            {viewMode === 'agg' ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                    <th className="text-left  px-5 py-2.5 font-semibold" rowSpan={2}>Direzione</th>
                    <th className="text-right px-3 py-2.5 font-semibold" rowSpan={2}>N. Filiali</th>
                    <th className="text-right px-3 py-2.5 font-semibold" rowSpan={2}>A Premio</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-amber-500" rowSpan={2}>ICF Rischio Medio</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-amber-500" rowSpan={2}>%</th>
                    <th className="text-center px-3 py-1.5 font-semibold text-red-500 border-l border-red-100" colSpan={5}>
                      Rischio alto per ambito normativo
                    </th>
                  </tr>
                  <tr className="bg-gray-50 text-red-400 uppercase text-[8px] tracking-wide">
                    <th className="text-right px-3 py-1.5 font-semibold border-l border-red-100">Usura</th>
                    <th className="text-right px-3 py-1.5 font-semibold">AML</th>
                    <th className="text-right px-3 py-1.5 font-semibold">Serv. Investimento</th>
                    <th className="text-right px-3 py-1.5 font-semibold">Trasparenza</th>
                    <th className="text-right px-3 py-1.5 font-semibold">IVASS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {section.rows.map((row, i) => (
                    <motion.tr key={i} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-5 py-3 font-medium text-[#1B365D] whitespace-nowrap">
                        {isEditing ? <input type="text" className={txtCls} value={row.regional_division} onChange={e => updateRowName(i, e.target.value)} /> : row.regional_division}
                      </td>
                      <td className="px-3 py-2.5 text-right text-gray-600">
                        {isEditing ? <input type="number" className={numCls} value={row.branches} onChange={e => updateRow(i, 'branches', e.target.value)} /> : fmtN(row.branches)}
                      </td>
                      <td className="px-3 py-2.5 text-right text-gray-600">
                        {isEditing ? <input type="number" className={numCls} value={row.awarded_branches} onChange={e => updateRow(i, 'awarded_branches', e.target.value)} /> : fmtN(row.awarded_branches)}
                      </td>
                      <td className="px-3 py-3 text-right"><RiskCell n={row.icf_medium_risk}          isEditing={isEditing} onChange={v => updateRow(i, 'icf_medium_risk',          v)} /></td>
                      <td className="px-3 py-3 text-right text-[10px] text-gray-500">
                        {isEditing ? <input type="number" className={numClsSm} value={row.icf_medium_risk_pct} onChange={e => updateRow(i, 'icf_medium_risk_pct', e.target.value)} /> : fmtPct2(row.icf_medium_risk_pct)}
                      </td>
                      <td className="px-3 py-3 text-right border-l border-red-100"><RiskCell n={row.usury_high}               isEditing={isEditing} onChange={v => updateRow(i, 'usury_high',               v)} /></td>
                      <td className="px-3 py-3 text-right"><RiskCell n={row.aml_high}                 isEditing={isEditing} onChange={v => updateRow(i, 'aml_high',                 v)} /></td>
                      <td className="px-3 py-3 text-right"><RiskCell n={row.investment_services_high} isEditing={isEditing} onChange={v => updateRow(i, 'investment_services_high', v)} /></td>
                      <td className="px-3 py-3 text-right"><RiskCell n={row.transparency_high}         isEditing={isEditing} onChange={v => updateRow(i, 'transparency_high',         v)} /></td>
                      <td className="px-3 py-3 text-right"><RiskCell n={row.ivass_high}               isEditing={isEditing} onChange={v => updateRow(i, 'ivass_high',               v)} /></td>
                    </motion.tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-[#1B365D] text-white text-[11px] font-semibold">
                    <td className="px-5 py-3">
                      {isEditing ? <input type="text" className={txtCls} value={totals.regional_division} onChange={e => updateTotalsName(e.target.value)} /> : totals.regional_division}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {isEditing ? <input type="number" className={numCls} value={totals.branches} onChange={e => updateTotals('branches', e.target.value)} /> : fmtN(totals.branches)}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {isEditing ? <input type="number" className={numCls} value={totals.awarded_branches} onChange={e => updateTotals('awarded_branches', e.target.value)} /> : fmtN(totals.awarded_branches)}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {isEditing ? <input type="number" className={numClsSm} value={totals.icf_medium_risk} onChange={e => updateTotals('icf_medium_risk', e.target.value)} /> : <FooterCell n={totals.icf_medium_risk} />}
                    </td>
                    <td className="px-3 py-3 text-right text-white/70">
                      {isEditing ? <input type="number" className={numClsSm} value={totals.icf_medium_risk_pct} onChange={e => updateTotals('icf_medium_risk_pct', e.target.value)} /> : fmtPct2(totals.icf_medium_risk_pct)}
                    </td>
                    <td className="px-3 py-3 text-right border-l border-white/20">
                      {isEditing ? <input type="number" className={numClsSm} value={totals.usury_high} onChange={e => updateTotals('usury_high', e.target.value)} /> : <FooterCell n={totals.usury_high} />}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {isEditing ? <input type="number" className={numClsSm} value={totals.aml_high} onChange={e => updateTotals('aml_high', e.target.value)} /> : <FooterCell n={totals.aml_high} />}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {isEditing ? <input type="number" className={numClsSm} value={totals.investment_services_high} onChange={e => updateTotals('investment_services_high', e.target.value)} /> : <FooterCell n={totals.investment_services_high} />}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {isEditing ? <input type="number" className={numClsSm} value={totals.transparency_high} onChange={e => updateTotals('transparency_high', e.target.value)} /> : <FooterCell n={totals.transparency_high} />}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {isEditing ? <input type="number" className={numClsSm} value={totals.ivass_high} onChange={e => updateTotals('ivass_high', e.target.value)} /> : <FooterCell n={totals.ivass_high} />}
                    </td>
                  </tr>
                </tfoot>
              </table>
              {isEditing && (
                <div className="flex justify-end mt-2 px-5 pb-4">
                  <button
                    type="button"
                    onClick={() => addRow()}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
                  </button>
                </div>
              )}
            </div>
            ) : detailRows.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-400">
                Nessuna filiale nella popolazione sorgente per {isGlobal ? 'i segmenti analizzati' : 'questo segmento'}.
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                    {isGlobal && <th className="text-left px-5 py-2.5 font-semibold">Segmento</th>}
                    <th className="text-left  px-5 py-2.5 font-semibold">Filiale</th>
                    <th className="text-left  px-3 py-2.5 font-semibold">Direzione</th>
                    <th className="text-center px-3 py-2.5 font-semibold">A Premio</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-amber-500">ICF</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-red-400 border-l border-red-100">Usura</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-red-400">AML</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-red-400">Serv. Investimento</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-red-400">Trasparenza</th>
                    <th className="text-right px-3 py-2.5 font-semibold text-red-400">IVASS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {detailRows.map(({ segmentKey, index, row }, i) => (
                    <motion.tr
                      key={`${segmentKey}-${index}`}
                      initial={{ opacity: 0, x: -4 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: Math.min(i * 0.01, 0.4) }}
                      className="hover:bg-gray-50/60 transition-colors"
                    >
                      {isGlobal && <td className="px-5 py-3 text-[10px] text-gray-500 whitespace-nowrap">{tipoLabel(segmentKey)}</td>}
                      <td className="px-5 py-3 font-mono text-[10px] font-medium text-[#1B365D] whitespace-nowrap">
                        {isEditing ? <input type="text" className={txtCls} value={row.branch_code} onChange={e => updateSourceRowText(segmentKey, index, 'branch_code', e.target.value)} /> : row.branch_code}
                      </td>
                      <td className="px-3 py-3 text-[10px] text-gray-500 whitespace-nowrap">
                        {isEditing ? <input type="text" className={txtCls} value={row.regional_division} onChange={e => updateSourceRowText(segmentKey, index, 'regional_division', e.target.value)} /> : row.regional_division}
                      </td>
                      <td className="px-3 py-3 text-center whitespace-nowrap">
                        {isEditing
                          ? <input type="checkbox" checked={row.awarded} onChange={() => toggleAwarded(segmentKey, index)} className="accent-[#1B365D]" />
                          : <span className={`text-[10px] font-semibold ${row.awarded ? 'text-emerald-600' : 'text-gray-400'}`}>{row.awarded ? 'Sì' : 'No'}</span>}
                      </td>
                      <td className="px-3 py-3 text-right text-gray-700 font-semibold tabular-nums whitespace-nowrap">
                        {isEditing ? <input type="number" className={numClsSm} value={row.icf ?? ''} onChange={e => updateSourceRow(segmentKey, index, 'icf', e.target.value)} /> : fmtNn(row.icf)}
                      </td>
                      <td className="px-3 py-3 text-right text-gray-600 tabular-nums whitespace-nowrap border-l border-red-100">
                        {isEditing ? <input type="number" className={numClsSm} value={row.usura ?? ''} onChange={e => updateSourceRow(segmentKey, index, 'usura', e.target.value)} /> : fmtNn(row.usura)}
                      </td>
                      <td className="px-3 py-3 text-right text-gray-600 tabular-nums whitespace-nowrap">
                        {isEditing ? <input type="number" className={numClsSm} value={row.aml ?? ''} onChange={e => updateSourceRow(segmentKey, index, 'aml', e.target.value)} /> : fmtNn(row.aml)}
                      </td>
                      <td className="px-3 py-3 text-right text-gray-600 tabular-nums whitespace-nowrap">
                        {isEditing ? <input type="number" className={numClsSm} value={row.investimenti ?? ''} onChange={e => updateSourceRow(segmentKey, index, 'investimenti', e.target.value)} /> : fmtNn(row.investimenti)}
                      </td>
                      <td className="px-3 py-3 text-right text-gray-600 tabular-nums whitespace-nowrap">
                        {isEditing ? <input type="number" className={numClsSm} value={row.trasparenza ?? ''} onChange={e => updateSourceRow(segmentKey, index, 'trasparenza', e.target.value)} /> : fmtNn(row.trasparenza)}
                      </td>
                      <td className="px-3 py-3 text-right text-gray-600 tabular-nums whitespace-nowrap">
                        {isEditing ? <input type="number" className={numClsSm} value={row.ivass ?? ''} onChange={e => updateSourceRow(segmentKey, index, 'ivass', e.target.value)} /> : fmtNn(row.ivass)}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
              {isEditing && !isGlobal && (
                <div className="flex justify-end mt-2 px-5 pb-4">
                  <button
                    type="button"
                    onClick={() => addSourceRow(activeTab.key)}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
                  </button>
                </div>
              )}
            </div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default CorrelazioneICFContainer;
